// src/components/AdminPanelUserList.jsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { db } from '../../../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  Timestamp,
} from 'firebase/firestore';

/* -------------------------
Small UI primitives (Modal, Badge)
------------------------- */
function Modal({ title, open, onClose, children }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // focus first input inside modal if any
    setTimeout(() => {
      if (ref.current) {
        const focusable = ref.current.querySelector('input,select,button,textarea');
        if (focusable) focusable.focus();
      }
    }, 0);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div
        className="relative z-10 max-w-2xl w-full bg-white rounded-lg shadow-lg p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        ref={ref}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 id="modal-title" className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-900" type="button">✕</button>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}


/* -------------------------
Helpers
------------------------- */
function toDateObj(tsOrDate) {
  if (!tsOrDate) return null;
  if (typeof tsOrDate.toDate === 'function') return tsOrDate.toDate(); // Firestore Timestamp
  if (tsOrDate instanceof Date) return tsOrDate;
  const parsed = new Date(tsOrDate);
  return isNaN(parsed) ? null : parsed;
}

function useIsMounted() {
  const ref = useRef(false);
  useEffect(() => { ref.current = true; return () => { ref.current = false; }; }, []);
  return ref;
}

const calculateDaysSince = (value) => {
  const date = toDateObj(value);
  if (!date) return null;
  const now = new Date();
  const diffTime = now - date;
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

const formatDate = (tsOrDate) => {
  const d = toDateObj(tsOrDate);
  if (!d) return null;
  return d.toLocaleDateString();
};

const formatTime = (tsOrDate) => {
  const d = toDateObj(tsOrDate);
  if (!d) return null;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

function timeToInputValue(t) {
  if (!t) return '';
  const hhmm = t.match(/^(\d{1,2}):(\d{2})/);
  if (hhmm) {
    const h = hhmm[1].padStart(2,'0');
    const m = hhmm[2];
    return `${h}:${m}`;
  }
  const ampm = t.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)/);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = ampm[2] ? ampm[2] : '00';
    const pm = /pm/i.test(ampm[3]);
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
    return `${String(h).padStart(2,'0')}:${m}`;
  }
  const parsed = new Date(`1970-01-01T${t}`);
  if (!isNaN(parsed)) return `${String(parsed.getHours()).padStart(2,'0')}:${String(parsed.getMinutes()).padStart(2,'0')}`;
  return '';
}

function computeEndTimeString(startTimeStr, durationHours) {
  if (!startTimeStr || !durationHours) return null;
  const parts = startTimeStr.split(':').map(Number);
  if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return null;
  const [h, m] = parts;
  const base = new Date();
  base.setHours(h, m, 0, 0);
  const end = new Date(base.getTime() + (Number(durationHours) || 0) * 60 * 60 * 1000);
  const sameDay = base.toDateString() === end.toDateString();
  const endStr = `${String(end.getHours()).padStart(2,'0')}:${String(end.getMinutes()).padStart(2,'0')}`;
  return { endStr, plusOneDay: !sameDay };
}

/* -------------------------
Calendar with month navigation + day modal
------------------------- */
function CalendarWithModal({ events }) {
  const [current, setCurrent] = useState(() => {
    const t = new Date();
    return { year: t.getFullYear(), month: t.getMonth() };
  });
  const [dayModal, setDayModal] = useState({ open: false, date: null, events: [] });

  const eventsMap = useMemo(() => {
    const m = {};
    events.forEach((e) => {
      if (e.sesja) {
        const sesjaDate = toDateObj(e.sesja);
        if (!sesjaDate) return;
        const key = sesjaDate.toDateString();
        m[key] = m[key] || [];
        m[key].push({ event: e, date: sesjaDate });
      }
    });
    return m;
  }, [events]);

  const firstDay = new Date(current.year, current.month, 1);
  const startDay = firstDay.getDay();
  const daysInMonth = new Date(current.year, current.month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(current.year, current.month, day));

  const openDay = (date) => {
    const key = date.toDateString();
    setDayModal({ open: true, date, events: eventsMap[key] || [] });
  };

  return (
    <div className="border rounded p-4 bg-white">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrent(c => {
            const m = c.month - 1;
            if (m < 0) return { year: c.year - 1, month: 11 };
            return { ...c, month: m };
          })} className="px-2 py-1 rounded border" type="button">‹</button>
          <div className="font-semibold">{new Date(current.year, current.month).toLocaleString('default', { month: 'long', year: 'numeric' })}</div>
          <button onClick={() => setCurrent(c => {
            const m = c.month + 1;
            if (m > 11) return { year: c.year + 1, month: 0 };
            return { ...c, month: m };
          })} className="px-2 py-1 rounded border" type="button">›</button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-sm">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
          <div key={d} className="text-center text-xs text-gray-500">{d}</div>
        ))}

        {cells.map((date, i) => {
          if (!date) return <div key={i} className="h-16" />;
          const key = date.toDateString();
          const ev = eventsMap[key] || [];
          const hasEvent = ev.length > 0;

          const tooltip = ev.slice(0,3).map(p => {
            const e = p.event;
            const t = e.sessionTime || formatTime(p.date) || '';
            return `${e.fullName || 'Wydarzenie'} • ${t} ${e.sessionLocation || ''}`.trim();
          }).join('\n');

          return (
            <div key={i} className="h-16 p-1 border rounded flex flex-col justify-between cursor-pointer hover:bg-gray-50" onClick={() => openDay(date)} title={tooltip}>
              <div className="text-right text-xs">{date.getDate()}</div>
              <div className="flex gap-1 items-end">
                {hasEvent && <span className="block w-2 h-2 rounded-full bg-blue-600" />}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-xs text-gray-600">
        <div className="flex items-center gap-2"><span className="w-2 h-2 bg-blue-600 rounded-full" /> wydarzenie</div>
      </div>

      {dayModal.open && (
        <Modal title={`Wydarzenia — ${dayModal.date.toLocaleDateString()}`} open={dayModal.open} onClose={() => setDayModal({ open: false, date: null, events: [] })}>
          <div className="space-y-3">
            {dayModal.events.length === 0 ? <p className="text-sm text-gray-500">Brak wydarzeń</p> : (
              dayModal.events.map((p, idx) => {
                const e = p.event;
                return (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="font-semibold">{e.fullName}</div>
                    <div className="text-sm text-gray-600">{e.sessionTime ? `${e.sessionTime} • ${e.sessionDuration || '-'}h` : formatTime(p.date)}</div>
                    {e.sessionLocation && <div className="text-sm">📍 {e.sessionLocation}</div>}
                  </div>
                );
              })
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* -------------------------
Main component
------------------------- */
export default function AdminPanelUserList() {
  const [events, setEvents] = useState([]); // docs from 'users' collection treated as events
  const [loading, setLoading] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // modal state: { type: null|'edit'|'payment'|'add', open: bool, target: eventObj }
  const [modal, setModal] = useState({ type: null, open: false, target: null });

  // operation states
  const [opLoading, setOpLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const messageTimeoutRef = useRef(null);
  const isMounted = useIsMounted();

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCounter]);

  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
  }, []);

  const clearMessage = () => {
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    messageTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setMessage(null);
    }, 3500);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEvents(list);
    } catch (err) {
      console.error('fetchAll failed', err);
      setMessage({ type: 'error', text: 'Błąd pobierania danych' });
      clearMessage();
    } finally {
      setLoading(false);
    }
  };

  const updateLocalEvent = (id, patchFn) => {
    setEvents(prev => prev.map(e => {
      if (e.id !== id) return e;
      const copy = { ...e };
      const patched = typeof patchFn === 'function' ? patchFn(copy) : { ...copy, ...patchFn };
      return patched;
    }));
  };

  /* -------------------------
  Actions: add / edit / delete event
  ------------------------- */
  const openModal = (type, target = null) => setModal({ type, open: true, target });
  const closeModal = () => setModal({ type: null, open: false, target: null });

  const addEvent = async ({ title, date, time, duration, location }) => {
    setOpLoading(true);
    try {
      const dt = date ? new Date(`${date}T${time || '00:00'}`) : null;
      const docObj = {
        fullName: title || 'Wydarzenie',
        sesja: dt ? Timestamp.fromDate(dt) : null,
        sessionTime: time || null,
        sessionDuration: duration ? Number(duration) : null,
        sessionLocation: location || null,
        payment: null,
      };
      const ref = await addDoc(collection(db, 'users'), docObj);
      const newDoc = { id: ref.id, ...docObj };
      setEvents(prev => [newDoc, ...prev]);
      setMessage({ type: 'success', text: 'Wydarzenie dodane' });
      clearMessage();
    } catch (err) {
      console.error('addEvent failed', err);
      setMessage({ type: 'error', text: 'Błąd dodawania wydarzenia' });
      clearMessage();
    } finally {
      setOpLoading(false);
      closeModal();
    }
  };

  const saveFullSession = async (id, { title, date, time, duration, location }) => {
    setOpLoading(true);
    try {
      const dt = date ? new Date(`${date}T${time || '00:00'}`) : null;
      const patch = {
        fullName: title,
        sessionTime: time || null,
        sessionDuration: duration ? Number(duration) : null,
        sessionLocation: location || null,
        sesja: dt ? Timestamp.fromDate(dt) : null,
      };
      const ref = doc(db, 'users', id);
      await updateDoc(ref, patch);
      updateLocalEvent(id, (e) => ({ ...e, ...patch }));
      setMessage({ type: 'success', text: 'Wydarzenie zapisane' });
      clearMessage();
    } catch (err) {
      console.error('saveFullSession failed', err);
      setMessage({ type: 'error', text: 'Błąd zapisu wydarzenia' });
      clearMessage();
    } finally {
      setOpLoading(false);
      closeModal();
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Na pewno usunąć to wydarzenie?')) return;
    setOpLoading(true);
    try {
      await deleteDoc(doc(db, 'users', id));
      setEvents(prev => prev.filter(e => e.id !== id));
      setMessage({ type: 'success', text: 'Wydarzenie usunięte' });
      clearMessage();
    } catch (err) {
      console.error('deleteEvent failed', err);
      setMessage({ type: 'error', text: 'Błąd usuwania' });
      clearMessage();
    } finally {
      setOpLoading(false);
      closeModal();
    }
  };

  /* -------------------------
  Payment helpers (kept from poprzedniego komponentu)
  ------------------------- */
  const savePaymentSetup = async (id, paymentObj) => {
    setOpLoading(true);
    try {
      const ref = doc(db, 'users', id);
      await updateDoc(ref, { payment: paymentObj });
      updateLocalEvent(id, (u) => ({ ...u, payment: paymentObj }));
      setMessage({ type: 'success', text: 'Płatność zapisana' });
      clearMessage();
    } catch (err) {
      console.error('savePaymentSetup failed', err);
      setMessage({ type: 'error', text: 'Błąd zapisu płatności' });
      clearMessage();
    } finally {
      setOpLoading(false);
      closeModal();
    }
  };

  const markInstallmentPaid = async (id, idx) => {
    setOpLoading(true);
    try {
      const e = events.find(x => x.id === id);
      if (!e || !e.payment || !Array.isArray(e.payment.installments)) throw new Error('No installments');
      const installments = e.payment.installments.map((it, i) => i === idx ? { ...it, paid: true, paidDate: Timestamp.now() } : it);
      const ref = doc(db, 'users', id);
      await updateDoc(ref, { 'payment.installments': installments });
      updateLocalEvent(id, (u) => ({ ...u, payment: { ...u.payment, installments } }));
      setMessage({ type: 'success', text: 'Rata oznaczona jako zapłacona' });
      clearMessage();
    } catch (err) {
      console.error('markInstallmentPaid failed', err);
      setMessage({ type: 'error', text: 'Błąd zapisu płatności' });
      clearMessage();
    } finally {
      setOpLoading(false);
    }
  };

  const undoInstallmentPaid = async (id, idx) => {
    setOpLoading(true);
    try {
      const e = events.find(x => x.id === id);
      if (!e || !e.payment || !Array.isArray(e.payment.installments)) throw new Error('No installments');
      const installments = e.payment.installments.map((it, i) => i === idx ? { ...it, paid: false, paidDate: null } : it);
      const ref = doc(db, 'users', id);
      await updateDoc(ref, { 'payment.installments': installments });
      updateLocalEvent(id, (u) => ({ ...u, payment: { ...u.payment, installments } }));
      setMessage({ type: 'success', text: 'Status raty cofnięty' });
      clearMessage();
    } catch (err) {
      console.error('undoInstallmentPaid failed', err);
      setMessage({ type: 'error', text: 'Błąd cofania płatności' });
      clearMessage();
    } finally {
      setOpLoading(false);
    }
  };

  /* -------------------------
  EventCard component
  ------------------------- */
  const EventCard = React.memo(({ event }) => {
    const sesja = toDateObj(event.sesja);
    const daysSinceSession = event.sesja ? calculateDaysSince(event.sesja) : null;

    const payment = event.payment || null;
    const totalPaid = payment && Array.isArray(payment.installments) ? payment.installments.filter(i => i.paid).reduce((a,b)=>a+(b.amount||0),0) : 0;
    const totalDue = payment ? (payment.total || 0) - totalPaid : 0;
    const paymentState = payment ? (totalDue <= 0 ? 'paid' : (totalPaid > 0 ? 'partial' : 'unpaid')) : 'none';

    return (
      <div className="border rounded-xl p-4 shadow-sm bg-white">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <p className="text-lg font-semibold">{event.fullName || '—'}</p>
            <p className="text-xs text-gray-500">ID: {event.id}</p>

            <div className="mt-3 space-y-1 text-sm text-gray-700">
              <p>
                <strong>Data:</strong>{' '}
                {sesja ? <span>{sesja.toLocaleDateString()} {event.sessionTime ? `• ${event.sessionTime}` : ''}</span> : <span className="text-red-600 font-semibold">brak daty</span>}
              </p>

              {sesja && <p><strong>Dni od daty:</strong> {daysSinceSession}</p>}
              {event.sessionLocation && <p><strong>Miejsce:</strong> {event.sessionLocation} {event.sessionDuration ? `• ${event.sessionDuration}h` : ''}</p>}

              {payment && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className={`px-2 py-1 rounded text-sm font-medium ${paymentState === 'paid' ? 'bg-green-100 text-green-800' : (paymentState === 'partial' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800')}`}>💳 {payment.total} zł</div>
                    <div className="text-xs text-gray-600">{paymentState === 'paid' ? 'Opłacone' : paymentState === 'partial' ? `Częściowo (do zapłaty ${totalDue} zł)` : `Nieopłacone (do zapłaty ${totalDue} zł)`}</div>
                  </div>
                  {Array.isArray(payment.installments) && (
                    <div className="mt-2 space-y-1 text-xs">
                      {payment.installments.map((it, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div>{idx+1}. {it.amount} zł — {it.paid ? `zapłacono (${it.paidDate ? formatDate(it.paidDate) : ''})` : 'niezapłacone'}</div>
                          <div className="flex items-center gap-2">
                            {!it.paid && <button onClick={() => markInstallmentPaid(event.id, idx)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs" type="button">Oznacz jako zapłacone</button>}
                            {it.paid && <button onClick={() => undoInstallmentPaid(event.id, idx)} className="px-2 py-1 bg-yellow-400 text-black rounded text-xs" type="button">Cofnij</button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 flex gap-2">
              <button onClick={() => openModal('edit', event)} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm">✏️ Edytuj</button>
              <button onClick={() => openModal('payment', event)} className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-sm">💳 Płatność</button>
              <button onClick={() => deleteEvent(event.id)} disabled={opLoading} className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm">🗑️ Usuń</button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  /* -------------------------
  Modals: Add / Edit Session / Payment
  ------------------------- */
  const AddEventModal = () => {
    const [title, setTitle] = useState('');
    const [dateVal, setDateVal] = useState('');
    const [timeVal, setTimeVal] = useState('10:00');
    const [duration, setDuration] = useState(6);
    const [location, setLocation] = useState('');

    const canSave = title.trim().length > 0;

    return (
      <Modal title="Dodaj wydarzenie" open={modal.open && modal.type === 'add'} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block font-medium">Tytuł / nazwa wydarzenia:</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} type="text" className="border px-3 py-2 rounded w-full" />

          <label className="block font-medium">Data:</label>
          <input value={dateVal} onChange={(e) => setDateVal(e.target.value)} type="date" className="border px-3 py-2 rounded w-full" />

          <label className="block font-medium">Godzina:</label>
          <input value={timeVal} onChange={(e) => setTimeVal(e.target.value)} type="time" className="border px-3 py-2 rounded w-full" />

          <label className="block font-medium">Czas trwania (godziny):</label>
          <input value={duration} onChange={(e) => setDuration(e.target.value)} type="number" min={1} className="border px-3 py-2 rounded w-40" />

          <label className="block font-medium">Miejsce:</label>
          <input value={location} onChange={(e) => setLocation(e.target.value)} type="text" className="border px-3 py-2 rounded w-full" />

          <div className="flex gap-2 justify-end">
            <button onClick={closeModal} className="px-4 py-2 rounded border" type="button">Anuluj</button>
            <button onClick={() => addEvent({ title, date: dateVal, time: timeVal, duration, location })} disabled={!canSave || opLoading} className="px-4 py-2 rounded bg-green-600 text-white">{opLoading ? 'Dodawanie...' : 'Dodaj'}</button>
          </div>
        </div>
      </Modal>
    );
  };

  const SessionFullModal = () => {
    const event = modal.target;
    const initialDate = event?.sesja ? (toDateObj(event.sesja).toISOString().slice(0,10)) : '';
    const initialTime = event?.sessionTime || (event?.sesja ? (toDateObj(event.sesja) ? formatTime(event.sesja) : '') : '');
    const [title, setTitle] = useState(event?.fullName || '');
    const [dateVal, setDateVal] = useState(initialDate);
    const [timeVal, setTimeVal] = useState(initialTime ? timeToInputValue(initialTime) : '10:00');
    const [duration, setDuration] = useState(event?.sessionDuration || 6);
    const [location, setLocation] = useState(event?.sessionLocation || '');

    useEffect(() => {
      setTitle(event?.fullName || '');
      setDateVal(event?.sesja ? toDateObj(event.sesja).toISOString().slice(0,10) : '');
      setTimeVal(event?.sessionTime ? timeToInputValue(event.sessionTime) : (event?.sesja ? timeToInputValue(formatTime(event.sesja)) : '10:00'));
      setDuration(event?.sessionDuration || 6);
      setLocation(event?.sessionLocation || '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?.id]);

    const chosenDuration = Number(duration || 0);
    const endInfo = computeEndTimeString(timeVal, chosenDuration);
    const valid = title.trim().length > 0 && dateVal && timeVal && chosenDuration > 0;

    return (
      <Modal title={`Edytuj wydarzenie — ${event?.fullName || ''}`} open={modal.open && modal.type === 'edit'} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block font-medium">Tytuł / nazwa:</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} type="text" className="border px-3 py-2 rounded w-full" />

          <label className="block font-medium">Data:</label>
          <input value={dateVal} onChange={(e)=>setDateVal(e.target.value)} type="date" className="border px-3 py-2 rounded w-full" />

          <label className="block font-medium">Godzina startu:</label>
          <input value={timeVal} onChange={(e)=>setTimeVal(e.target.value)} type="time" className="border px-3 py-2 rounded w-full" />

          <label className="block font-medium">Czas trwania:</label>
          <input value={duration} onChange={(e)=>setDuration(e.target.value)} type="number" min={1} className="border px-3 py-2 rounded w-40" />

          <div className="text-sm text-gray-600">Koniec sesji: {endInfo ? `${endInfo.endStr}${endInfo.plusOneDay ? ' (+1 dzień)' : ''}` : '—'}</div>

          <label className="block font-medium">Miejsce:</label>
          <input value={location} onChange={(e)=>setLocation(e.target.value)} type="text" className="border px-3 py-2 rounded w-full" />

          <div className="flex gap-2 justify-end">
            <button onClick={closeModal} className="px-4 py-2 rounded border" type="button">Anuluj</button>
            <button onClick={() => saveFullSession(event.id, { title, date: dateVal, time: timeVal, duration: chosenDuration, location })} disabled={opLoading || !valid} className="px-4 py-2 rounded bg-blue-600 text-white" type="button">{opLoading ? 'Zapis...' : 'Zapisz'}</button>
          </div>
        </div>
      </Modal>
    );
  };

  const PaymentModal = () => {
    const event = modal.target;
    const p = event?.payment || null;
    const [total, setTotal] = useState(p?.total || '');
    const [useInstallments, setUseInstallments] = useState(Array.isArray(p?.installments));
    const [numInstallments, setNumInstallments] = useState(p?.installments ? p.installments.length : 1);
    const [installments, setInstallments] = useState(
      p?.installments ? p.installments.map(it => ({ ...it })) : [{ amount: Number(p?.total) || 0, paid: false, paidDate: null }]
    );
    const [manualEdit, setManualEdit] = useState(false);

    useEffect(() => {
      setTotal(p?.total || '');
      setUseInstallments(Array.isArray(p?.installments));
      setNumInstallments(p?.installments ? p.installments.length : 1);
      setInstallments(p?.installments ? p.installments.map(it => ({ ...it })) : [{ amount: Number(p?.total) || 0, paid: false, paidDate: null }]);
      setManualEdit(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?.id, p?.total, p?.installments]);

    useEffect(() => {
      if (!useInstallments) return;
      if (manualEdit) return;
      const t = Number(total) || 0;
      const n = Number(numInstallments) || 1;
      if (t > 0 && n > 0) {
        const base = Math.floor((t / n) * 100) / 100;
        const arr = new Array(n).fill(base);
        let sum = base * n;
        let rem = Math.round((t - sum) * 100) / 100;
        let i = 0;
        while (rem > 0.001 && i < n) {
          arr[i] = Math.round((arr[i] + 0.01) * 100) / 100;
          rem = Math.round((t - arr.reduce((a,b)=>a+b,0)) * 100) / 100;
          i++;
        }
        setInstallments(arr.map(a => ({ amount: a, paid: false, paidDate: null })));
      }
    }, [total, numInstallments, useInstallments, manualEdit]);

    const updateInstallmentAmount = (idx, val) => {
      setManualEdit(true);
      const copy = (installments || []).map(it => ({ ...it }));
      copy[idx] = { ...(copy[idx] || { amount: 0, paid: false, paidDate: null }), amount: Number(val) || 0 };
      setInstallments(copy);
    };

    const sumInstallments = (installments || []).reduce((s,it)=>s+(Number(it.amount)||0),0);
    const totalMismatch = Math.abs(sumInstallments - Number(total || 0)) > 0.01;

    const savePayments = () => {
      if (Number(total) <= 0) return setMessage({ type: 'error', text: 'Kwota musi być większa niż 0' });
      if (useInstallments && totalMismatch) return setMessage({ type: 'error', text: 'Suma rat różni się od całkowitej kwoty' });
      const paymentObj = { total: Number(total) || 0, installments: useInstallments ? installments : [{ amount: Number(total) || 0, paid: false, paidDate: null }] };
      savePaymentSetup(event.id, paymentObj);
    };

    return (
      <Modal title={`Ustaw płatność - ${event?.fullName || ''}`} open={modal.open && modal.type === 'payment'} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block font-medium">Całkowita kwota (PLN):</label>
          <input value={total} onChange={(e)=>setTotal(e.target.value)} type="number" className="border px-3 py-2 rounded w-full" />

          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={useInstallments} onChange={(e)=>setUseInstallments(e.target.checked)} /> Płatność w ratach</label>

          {useInstallments && (
            <div className="space-y-2">
              <label className="block font-medium">Liczba rat:</label>
              <input type="number" min={1} value={numInstallments} onChange={(e)=>setNumInstallments(e.target.value)} className="border px-3 py-2 rounded w-24" />

              <div className="pt-2">
                <div className="text-sm font-medium mb-1">Raty (edytuj, jeśli potrzebujesz):</div>
                <div className="space-y-2">
                  {(installments || []).map((it, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="text-xs w-6">{idx+1}.</div>
                      <input type="number" value={it.amount} onChange={(e)=>updateInstallmentAmount(idx, e.target.value)} className="border px-3 py-1 rounded w-32 text-sm" />
                      <div className="text-xs text-gray-600">{it.paid ? `Zapłacono (${formatDate(it.paidDate)})` : 'Nie zapłacono'}</div>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-gray-600">Suma rat: {sumInstallments} zł {totalMismatch && <span className="text-red-600">(nie zgadza się z całkowitą kwotą)</span>}</div>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button onClick={closeModal} className="px-4 py-2 rounded border" type="button">Anuluj</button>
            <button onClick={savePayments} disabled={opLoading || !total || (useInstallments && totalMismatch)} className="px-4 py-2 rounded bg-green-600 text-white" type="button">{opLoading ? 'Zapis...' : 'Zapisz płatność'}</button>
          </div>
        </div>
      </Modal>
    );
  };

  /* -------------------------
  Render
  ------------------------- */
  return (
    <div className="p-6 mt-8 container max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Wydarzenia (kolekcja 'users')</h1>
        <div className="flex items-center gap-3">
          <button onClick={()=>setRefreshCounter(c=>c+1)} className="px-3 py-2 rounded border" type="button">Odśwież listę</button>
          <button onClick={() => openModal('add')} className="px-3 py-2 bg-green-600 text-white rounded">➕ Dodaj wydarzenie</button>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} role="status">
          {message.text}
        </div>
      )}

      {loading ? (
        <p>Ładowanie...</p>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="col-span-1 md:col-span-3 grid grid-cols-1 gap-4">
              <h3 className="font-semibold mb-2">Lista wydarzeń</h3>
              <div className="space-y-4">
                {events.length === 0 ? <p className="text-sm text-gray-500">Brak wydarzeń</p> : events.map(e => <EventCard key={e.id} event={e} />)}
              </div>
            </div>

            <div className="col-span-1">
              <CalendarWithModal events={events} />
              <div className="mt-4 border rounded p-3 bg-white">
                <h4 className="font-semibold mb-2">Brak daty</h4>
                {events.filter(e => !e.sesja).length === 0 ? <p className="text-sm text-gray-500">Brak</p> : (
                  <div className="space-y-2">
                    {events.filter(e => !e.sesja).map(u => (
                      <div key={u.id} className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">{u.fullName}</div>
                          <div className="text-xs text-gray-500">ID: {u.id}</div>
                        </div>
                        <div>
                          <button onClick={() => openModal('edit', u)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs" type="button">Ustaw</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {modal.type === 'add' && <AddEventModal />}
      {modal.type === 'edit' && <SessionFullModal />}
      {modal.type === 'payment' && <PaymentModal />}
    </div>
  );
}
